import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import * as turf from '@turf/turf';
import './MapEditor.css';

// Fix for default marker icons in Leaflet with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const MapEditor = ({ 
  initialGeoJson = null, 
  onGeoJsonChange, 
  height = '400px',
  showAdvanced = false,
  onAdvancedToggle,
  editMode: externalEditMode = null, // Allow external control of edit mode
  onPolygonCountClick, // Callback when polygon count is clicked
  editExistingHint = false, // When true, show hint for editing existing polygons
  editableProperties = true // When true, clicking a polygon shows a popup to edit its name/properties
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const drawControlRef = useRef(null);
  const layersRef = useRef([]);
  const featureGroupRef = useRef(null);
  const eventHandlersRef = useRef([]); // Store event handlers for cleanup
  const processedCoordsRef = useRef(new Set()); // Track processed layer coordinates to prevent duplicates
  const lastLoadedGeoJsonRef = useRef(null); // Track last loaded GeoJSON to prevent unnecessary reloads
  const isInternalUpdateRef = useRef(false); // Track if update is from internal edit
  const lastSentGeoJsonRef = useRef(null); // Track GeoJSON we sent to parent (popup/edit) to skip reload on echo
  const [editMode, setEditMode] = useState(externalEditMode !== null ? externalEditMode : true);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [totalArea, setTotalArea] = useState(0);
  const [featureCount, setFeatureCount] = useState(0);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Create map
    const map = L.map(mapRef.current, {
      center: [39.5, -105.5], // Default to Colorado
      zoom: 6,
      zoomControl: true
    });

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);

    // Create feature group for editable layers
    const featureGroup = L.featureGroup().addTo(map);
    featureGroupRef.current = featureGroup;

    // Ensure map is fully initialized before setting reference
    // Wait for map to be ready
    map.whenReady(() => {
      mapInstanceRef.current = map;
    });
    
    // Also set immediately in case whenReady doesn't fire
    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        // Stop any ongoing transitions
        try {
          mapInstanceRef.current.stop();
        } catch (e) {
          // Ignore errors if map is already destroyed
        }
        
        // Remove all event listeners
        mapInstanceRef.current.off();
        
        // Remove the map
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      
      // Clear feature group
      if (featureGroupRef.current) {
        featureGroupRef.current.clearLayers();
        featureGroupRef.current = null;
      }
      
      // Clear layers array
      layersRef.current = [];
      
      // Clear event handlers
      eventHandlersRef.current = [];
    };
  }, []);

  // Load initial GeoJSON
  useEffect(() => {
    if (!mapInstanceRef.current || !featureGroupRef.current) return;
    
    // If no GeoJSON, clear layers and return
    if (!initialGeoJson) {
      console.log('No initialGeoJson provided, clearing layers');
      clearLayers();
      lastLoadedGeoJsonRef.current = null;
      return;
    }

    // Validate GeoJSON structure
    if (initialGeoJson.type !== 'FeatureCollection' || !Array.isArray(initialGeoJson.features)) {
      console.warn('Invalid GeoJSON structure:', initialGeoJson);
      return;
    }

    // Prevent reloading if this is an internal update (from our own edits)
    if (isInternalUpdateRef.current) {
      console.log('Skipping loadGeoJsonToMap - internal update');
      isInternalUpdateRef.current = false;
      lastSentGeoJsonRef.current = null;
      return;
    }

    // Skip reload when receiving our own echo (property update from popup or geometry edit)
    if (lastSentGeoJsonRef.current) {
      const sentStr = JSON.stringify(lastSentGeoJsonRef.current);
      const receivedStr = JSON.stringify(initialGeoJson);
      if (sentStr === receivedStr) {
        console.log('Skipping loadGeoJsonToMap - echo of our own update');
        lastSentGeoJsonRef.current = null;
        return;
      }
      lastSentGeoJsonRef.current = null;
    }
    
    // Check if GeoJSON has actually changed
    const currentGeoJsonStr = JSON.stringify(initialGeoJson);
    const lastGeoJsonStr = lastLoadedGeoJsonRef.current ? JSON.stringify(lastLoadedGeoJsonRef.current) : null;
    
    if (currentGeoJsonStr === lastGeoJsonStr) {
      console.log('GeoJSON unchanged, skipping reload');
      return; // GeoJSON hasn't changed, don't reload
    }
    
    console.log('Loading GeoJSON to map', {
      featureCount: initialGeoJson.features?.length || 0,
      isNew: !lastLoadedGeoJsonRef.current,
      geojsonType: initialGeoJson.type,
      firstFeature: initialGeoJson.features?.[0]
    });
    
    // Store this as the last loaded GeoJSON
    lastLoadedGeoJsonRef.current = JSON.parse(JSON.stringify(initialGeoJson));
    
    const map = mapInstanceRef.current;
    
    try {
      // Validate GeoJSON
      if (initialGeoJson.type === 'FeatureCollection' && Array.isArray(initialGeoJson.features)) {
        loadGeoJsonToMap(initialGeoJson);
        
        // Fit bounds to all polygons using featureGroup
        // Use setTimeout to ensure map is fully initialized and layers are rendered
        // Call loadGeoJsonToMap first, then fit bounds after a delay
        setTimeout(() => {
          if (!mapInstanceRef.current || !featureGroupRef.current) return;
          
          try {
            // Stop any ongoing zoom transitions
            try {
              mapInstanceRef.current.stop();
            } catch (e) {
              // Ignore if no transition is active
            }
            
            // Wait a bit more to ensure all layers are fully rendered
            const fitBoundsToPolygons = () => {
              if (!mapInstanceRef.current || !featureGroupRef.current) return;
              
              const layers = featureGroupRef.current.getLayers();
              const layersRefCount = layersRef.current.length;
              console.log('Fitting bounds to polygons', { 
                layerCount: layers.length, 
                layersRefCount: layersRefCount,
                featureGroupHasLayers: featureGroupRef.current.hasLayer ? 'hasLayer method exists' : 'no hasLayer method'
              });
              
              if (layers.length === 0) {
                console.warn('No layers in featureGroup to fit bounds', {
                  layersRefCount: layersRefCount,
                  featureGroup: featureGroupRef.current,
                  mapHasFeatureGroup: mapInstanceRef.current.hasLayer ? mapInstanceRef.current.hasLayer(featureGroupRef.current) : 'unknown'
                });
                // Retry if we have layers in layersRef but not in featureGroup
                if (layersRefCount > 0) {
                  console.log('Retrying fitBounds - layers in layersRef but not in featureGroup');
                  setTimeout(fitBoundsToPolygons, 300);
                }
                return;
              }
              
              try {
                const groupBounds = featureGroupRef.current.getBounds();
                if (groupBounds) {
                  // Check if bounds is valid
                  const isValid = typeof groupBounds.isValid === 'function' 
                    ? groupBounds.isValid() 
                    : (groupBounds.getNorth && groupBounds.getSouth && 
                        groupBounds.getEast && groupBounds.getWest &&
                        groupBounds.getNorth() !== groupBounds.getSouth() &&
                        groupBounds.getEast() !== groupBounds.getWest());
                  
                  if (isValid) {
                    console.log('Fitting bounds:', {
                      north: groupBounds.getNorth(),
                      south: groupBounds.getSouth(),
                      east: groupBounds.getEast(),
                      west: groupBounds.getWest()
                    });
                    mapInstanceRef.current.fitBounds(groupBounds, { padding: [50, 50] });
                  } else {
                    console.warn('Invalid bounds from featureGroup, trying individual layers');
                    // Fallback: try individual layer bounds
                    const bounds = new L.LatLngBounds();
                    let hasValidBounds = false;
                    
                    layers.forEach(layer => {
                      try {
                        if (!layer || !layer.getBounds) return;
                        const layerBounds = layer.getBounds();
                        if (layerBounds) {
                          bounds.extend(layerBounds);
                          hasValidBounds = true;
                        }
                      } catch (e) {
                        console.warn('Error getting bounds from layer:', e);
                      }
                    });
                    
                    if (hasValidBounds) {
                      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
                    }
                  }
                } else {
                  console.warn('No bounds from featureGroup');
                }
              } catch (error) {
                console.warn('Error fitting bounds:', error);
              }
            };
            
            // Try after a delay to ensure loadGeoJsonToMap has completed
            // loadGeoJsonToMap is called synchronously above, but we need to wait for layers to be added
            setTimeout(() => {
              fitBoundsToPolygons();
              // Retry multiple times with increasing delays
              setTimeout(fitBoundsToPolygons, 200);
              setTimeout(fitBoundsToPolygons, 500);
              setTimeout(fitBoundsToPolygons, 800);
            }, 300);
          } catch (error) {
            console.warn('Error in fitBounds setTimeout:', error);
          }
        }, 200);
        
        saveToHistory();
      }
    } catch (error) {
      console.error('Error loading GeoJSON:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGeoJson]);

  // Sync internal editMode with external prop if provided
  useEffect(() => {
    if (externalEditMode !== null && externalEditMode !== editMode) {
      setEditMode(externalEditMode);
    }
  }, [externalEditMode, editMode]);

  // Setup drawing controls when edit mode changes
  useEffect(() => {
    if (!mapInstanceRef.current || !featureGroupRef.current) return;

    const map = mapInstanceRef.current;

    if (editMode) {
      // Enable drawing - ensure map is ready first
      const setupDrawControl = () => {
        if (!mapInstanceRef.current || !featureGroupRef.current) return;
        if (drawControlRef.current) return; // Already exists
        
        const map = mapInstanceRef.current;
        
        // Ensure map control containers are initialized
        if (!map._controlContainer || !map._controlCorners) {
          console.log('Map control containers not ready, retrying...');
          setTimeout(setupDrawControl, 100);
          return;
        }
        
        // Ensure the topleft corner exists
        if (!map._controlCorners.topleft) {
          console.log('Map topleft corner not ready, retrying...');
          setTimeout(setupDrawControl, 100);
          return;
        }
        
        const drawControl = new L.Control.Draw({
          position: 'topleft', // Position on left to avoid overlap with action buttons
          draw: {
            polygon: {
              allowIntersection: false,
              showArea: true,
              drawError: {
                color: '#e1e100',
                message: '<strong>Oh snap!</strong> you can\'t draw that!'
              },
              shapeOptions: {
                color: '#3388ff',
                fillColor: '#3388ff',
                fillOpacity: 0.3
              }
            },
            polyline: false, // Disabled - not needed
            rectangle: false,
            circle: false,
            marker: false,
            circlemarker: false
          },
          edit: {
            featureGroup: featureGroupRef.current,
            remove: true,
            edit: true // Explicitly enable edit button
          }
        });

        try {
          drawControl.addTo(map);
          drawControlRef.current = drawControl;
        } catch (error) {
          console.error('Error adding draw control to map:', error);
          // Retry after a delay
          setTimeout(setupDrawControl, 200);
          return;
        }
        
        console.log('Draw control added to map', {
          hasMap: !!mapInstanceRef.current,
          hasFeatureGroup: !!featureGroupRef.current,
          controlPosition: drawControl.getPosition()
        });

        // Handle draw events - store handlers for cleanup
        const handleCreated = (e) => {
          if (!mapInstanceRef.current || !featureGroupRef.current) return;
          
          const { layer } = e;
          
          // Check if layer is a polygon or polyline
          if (!layer || !layer.getLatLngs) {
            console.warn('Created layer is not a valid polygon or polyline');
            return;
          }
          
          // CRITICAL: Prevent processing the same layer multiple times
          // Get coordinates for comparison
          const layerCoords = JSON.stringify(layer.getLatLngs());
          
          console.log('handleCreated called', {
            layerCoords: layerCoords.substring(0, 100),
            featureGroupLayers: featureGroupRef.current.getLayers().length,
            layersRefCount: layersRef.current.length,
            processedCoordsCount: processedCoordsRef.current.size,
            alreadyProcessed: processedCoordsRef.current.has(layerCoords),
            featureGroupHasThisLayer: featureGroupRef.current && featureGroupRef.current.hasLayer ? featureGroupRef.current.hasLayer(layer) : false,
            layersRefHasThisLayer: layersRef.current.includes(layer)
          });
          
          // Check if we've already processed a layer with these exact coordinates
          if (processedCoordsRef.current.has(layerCoords)) {
            console.log('Layer coordinates already processed, skipping duplicate handleCreated call');
            return;
          }
          
          // Check if a layer with the same coordinates already exists in featureGroup OR layersRef
          const existingLayersInFeatureGroup = featureGroupRef.current.getLayers();
          const existingLayersInRef = layersRef.current;
          
          // Check by reference first (fastest)
          const existsByRef = existingLayersInFeatureGroup.includes(layer) || existingLayersInRef.includes(layer);
          
          // Check by coordinates (in case reference differs)
          const existsByCoords = existingLayersInFeatureGroup.some(existingLayer => {
            if (existingLayer === layer) return false; // Already checked by reference
            if (!existingLayer || !existingLayer.getLatLngs) return false;
            try {
              return JSON.stringify(existingLayer.getLatLngs()) === layerCoords;
            } catch (e) {
              return false;
            }
          }) || existingLayersInRef.some(existingLayer => {
            if (existingLayer === layer) return false; // Already checked by reference
            if (!existingLayer || !existingLayer.getLatLngs) return false;
            try {
              return JSON.stringify(existingLayer.getLatLngs()) === layerCoords;
            } catch (e) {
              return false;
            }
          });
          
          if (existsByRef || existsByCoords) {
            console.log('Layer already processed (by reference or coordinates), skipping duplicate handleCreated call', {
              existsByRef,
              existsByCoords,
              featureGroupHasLayer: featureGroupRef.current && featureGroupRef.current.hasLayer ? featureGroupRef.current.hasLayer(layer) : false,
              layersRefHasLayer: layersRef.current.includes(layer)
            });
            // Mark as processed even though we're skipping
            processedCoordsRef.current.add(layerCoords);
            return;
          }
          
          // Mark these coordinates as processed NOW, before doing anything else
          processedCoordsRef.current.add(layerCoords);
          console.log('Marked layer coordinates as processed');
          
          // Ensure the layer has coordinates
          const latlngs = layer.getLatLngs();
          if (!latlngs || latlngs.length === 0) {
            console.warn('Created layer has no coordinates');
            return;
          }
          
          // IMPORTANT: Leaflet.draw automatically adds the layer to the map
          // We need to ensure it stays visible and is properly tracked
          
          // Style the polygon
          // First, ensure the layer is visible and styled correctly
          layer.setStyle({
            color: '#3388ff',
            weight: 2,
            fillColor: '#3388ff',
            fillOpacity: 0.3,
            opacity: 1.0
          });
          
          // Fallback: ensure basic styling even if above fails
          if (!layer.options || !layer.options.color) {
            layer.setStyle({
              color: '#3388ff',
              weight: 2,
              opacity: 1.0
            });
          }
          
          // Make sure layer is visible
          if (layer.setOpacity) {
            layer.setOpacity(1.0);
          }
          if (layer.setZIndex) {
            layer.setZIndex(1000); // Ensure it's on top
          }
          
          // CRITICAL: Leaflet.draw automatically adds layers to the featureGroup when created
          // (because we specified featureGroup in the edit config), so we should NOT add it again
          // Just verify it's there and clean up any duplicates
          
          // CRITICAL: Leaflet.draw automatically adds the layer to featureGroup when created
          // (because we specified featureGroup in the edit config)
          // To prevent duplicates, we need to:
          // 1. Remove the layer from featureGroup first (in case Leaflet.draw added it)
          // 2. Remove any duplicate layers with the same coordinates
          // 3. Add the layer back (only once)
          
          // CRITICAL: Leaflet.draw automatically adds the layer to featureGroup when created
          // (because we specified featureGroup in the edit config)
          // We should NOT try to add it ourselves - just check for duplicates and track it
          
          // Get the current layer's coordinates as a string for comparison
          const currentLayerCoords = JSON.stringify(layer.getLatLngs());
          
          // Step 1: Find ALL layers in featureGroup with the same coordinates
          const allLayers = featureGroupRef.current.getLayers().slice(); // Create a copy
          const matchingLayers = allLayers.filter(l => {
            if (!l || l === layer || !l.getLatLngs) return false; // Exclude the current layer
            try {
              const lCoords = JSON.stringify(l.getLatLngs());
              return lCoords === currentLayerCoords;
            } catch (e) {
              return false;
            }
          });
          
          // Step 2: Remove ONLY duplicate layers (NOT the current layer that Leaflet.draw added)
          if (matchingLayers.length > 0) {
            console.log(`Found ${matchingLayers.length} duplicate layer(s), removing`);
            matchingLayers.forEach(dupLayer => {
              try {
                if (featureGroupRef.current && featureGroupRef.current.hasLayer && featureGroupRef.current.hasLayer(dupLayer)) {
                  featureGroupRef.current.removeLayer(dupLayer);
                }
                const layerIndex = layersRef.current.indexOf(dupLayer);
                if (layerIndex > -1) {
                  layersRef.current.splice(layerIndex, 1);
                }
                if (mapInstanceRef.current && mapInstanceRef.current.hasLayer && mapInstanceRef.current.hasLayer(dupLayer)) {
                  mapInstanceRef.current.removeLayer(dupLayer);
                }
              } catch (e) {
                console.warn('Error removing duplicate layer:', e);
              }
            });
          }
          
          // Step 3: Verify the layer is in featureGroup (Leaflet.draw should have added it)
          // If not, add it (but this shouldn't happen)
          if (featureGroupRef.current && featureGroupRef.current.hasLayer && !featureGroupRef.current.hasLayer(layer)) {
            console.warn('Layer not in featureGroup (unexpected), adding it');
            featureGroupRef.current.addLayer(layer);
          } else {
            console.log('Layer already in featureGroup (added by Leaflet.draw)');
          }
          
          // Step 4: Track the layer in layersRef (for our own tracking)
          // Check by both reference AND coordinates to prevent duplicates
          const layerExists = layersRef.current.find(refLayer => {
            if (refLayer === layer) return true; // Same reference
            if (!refLayer || !refLayer.getLatLngs) return false;
            try {
              return JSON.stringify(refLayer.getLatLngs()) === currentLayerCoords;
            } catch (e) {
              return false;
            }
          });
          
          if (!layerExists) {
            layersRef.current.push(layer);
            console.log('Layer added to layersRef, total layers:', layersRef.current.length);
          } else {
            console.log('Layer already in layersRef (by reference or coordinates)');
          }
          
          // Function to ensure layer stays visible
          const ensureLayerVisible = () => {
            if (!mapInstanceRef.current || !featureGroupRef.current || !layer) return;
            
            // Check if layer is on map
            if (!mapInstanceRef.current || !featureGroupRef.current) return;
            const isOnMap = mapInstanceRef.current.hasLayer ? mapInstanceRef.current.hasLayer(layer) : false;
            const isInFeatureGroup = featureGroupRef.current.hasLayer ? featureGroupRef.current.hasLayer(layer) : false;
            
            if (!isOnMap || !isInFeatureGroup) {
              // Re-add to featureGroup (which adds it to map)
              if (!isInFeatureGroup) {
                featureGroupRef.current.addLayer(layer);
              }
              // Also explicitly add to map
              if (!isOnMap) {
                mapInstanceRef.current.addLayer(layer);
              }
              // Re-apply styling based on layer type
              const isPolygon = layer instanceof L.Polygon;
              if (isPolygon) {
                layer.setStyle({
                  color: '#3388ff',
                  weight: 2,
                  fillColor: '#3388ff',
                  fillOpacity: 0.3,
                  opacity: 1.0
                });
              } else {
                layer.setStyle({
                  color: '#3388ff',
                  weight: 2,
                  opacity: 1.0
                });
              }
              if (layer.setOpacity) {
                layer.setOpacity(1.0);
              }
            }
            
            // Always ensure it's visible and on top
            if (layer.bringToFront) {
              layer.bringToFront();
            }
            if (layer.redraw) {
              layer.redraw();
            }
          };
          
          // Ensure visibility immediately and after delays
          ensureLayerVisible();
          setTimeout(ensureLayerVisible, 50);
          setTimeout(ensureLayerVisible, 200);
          
          // Force a redraw to ensure visibility
          if (layer.redraw) {
            layer.redraw();
          }
          
          // Bring to front to ensure it's visible
          if (layer.bringToFront) {
            layer.bringToFront();
          }
          
          // Set up event handlers BEFORE enabling editing
          const handleEdit = () => {
            if (!mapInstanceRef.current) return;
            updateGeoJson();
            saveToHistory();
            // Update stats after edit is complete
            setTimeout(() => updateStats(), 0);
          };

          const handleDragEnd = () => {
            if (!mapInstanceRef.current) return;
            updateGeoJson();
            saveToHistory();
            setTimeout(() => updateStats(), 0);
          };

          // Allow deletion
          const handleContextMenu = (e) => {
            e.originalEvent.preventDefault();
            if (!mapInstanceRef.current || !featureGroupRef.current) return;
            if (window.confirm('Delete this polygon?')) {
              featureGroupRef.current.removeLayer(layer);
              const layerIndex = layersRef.current.indexOf(layer);
              if (layerIndex > -1) {
                layersRef.current.splice(layerIndex, 1);
              }
              // Remove from event handlers
              eventHandlersRef.current = eventHandlersRef.current.filter(
                h => !(h.type === 'layer' && h.layer === layer)
              );
              updateGeoJson();
              updateStats();
              saveToHistory();
            }
          };

          layer.on('edit', handleEdit);
          layer.on('dragend', handleDragEnd);
          layer.on('contextmenu', handleContextMenu);

          // Add click handler for properties popup (newly created polygons)
          if (editableProperties) {
            const handlePropertiesClick = (e) => {
              L.DomEvent.stopPropagation(e);
              const map = mapInstanceRef.current;
              if (!map || !(layer instanceof L.Polygon)) return;
              const currentName = layer.options?.properties?.name || layer.options?.properties?.name_en || '';
              const container = document.createElement('div');
              container.className = 'leaflet-popup-properties-edit';
              const safeValue = String(currentName || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
              container.innerHTML = `
                <label style="display:block;margin-bottom:6px;font-weight:600;font-size:13px">Location name</label>
                <input type="text" class="polygon-name-input" value="${safeValue}" placeholder="Enter location name" style="width:100%;padding:8px 10px;margin-bottom:10px;font-size:14px;border:1px solid #ddd;border-radius:4px;box-sizing:border-box" />
                <button type="button" class="polygon-properties-save-btn" style="padding:8px 16px;background:#3388ff;color:white;border:none;border-radius:4px;cursor:pointer;font-size:14px">Save</button>
              `;
              const input = container.querySelector('.polygon-name-input');
              const saveBtn = container.querySelector('.polygon-properties-save-btn');
              const popup = L.popup({ maxWidth: 280 }).setLatLng(e.latlng).setContent(container);
              saveBtn.onclick = () => {
                const name = input.value.trim();
                layer.options.properties = layer.options.properties || {};
                if (name) {
                  layer.options.properties.name = name;
                  layer.options.properties.locationName = name;
                } else {
                  delete layer.options.properties.name;
                  delete layer.options.properties.locationName;
                }
                popup.close();
                updateGeoJson();
              };
              popup.openOn(map);
            };
            layer.on('click', handlePropertiesClick);
            eventHandlersRef.current.push({
              type: 'layer',
              layer: layer,
              handlers: [
                { event: 'edit', handler: handleEdit },
                { event: 'dragend', handler: handleDragEnd },
                { event: 'contextmenu', handler: handleContextMenu },
                { event: 'click', handler: handlePropertiesClick }
              ]
            });
          } else {
            eventHandlersRef.current.push({
              type: 'layer',
              layer: layer,
              handlers: [
                { event: 'edit', handler: handleEdit },
                { event: 'dragend', handler: handleDragEnd },
                { event: 'contextmenu', handler: handleContextMenu }
              ]
            });
          }
          
          // Update GeoJSON and stats immediately to reflect the new polygon
          // Call immediately, then verify after a short delay
          updateGeoJson();
          updateStats();
          saveToHistory();
          
          // Verify after a short delay to ensure everything is correct
          setTimeout(() => {
            if (!mapInstanceRef.current || !featureGroupRef.current) return;
            // Double-check layer is still on the map
            const isInFeatureGroup = featureGroupRef.current.hasLayer ? featureGroupRef.current.hasLayer(layer) : false;
            const isInLayersRef = layersRef.current.includes(layer);
            const isOnMap = mapInstanceRef.current.hasLayer ? mapInstanceRef.current.hasLayer(layer) : false;
            
            // Get all layers in featureGroup to check for duplicates
            const allFeatureGroupLayers = featureGroupRef.current.getLayers();
            const currentLayerCoords = JSON.stringify(layer.getLatLngs());
            
            // Check for duplicates (layers with same coordinates but NOT the current layer)
            const duplicateLayers = allFeatureGroupLayers.filter(l => {
              if (!l || l === layer || !l.getLatLngs) return false; // Exclude current layer
              try {
                return JSON.stringify(l.getLatLngs()) === currentLayerCoords;
              } catch (e) {
                return false;
              }
            });
            
            console.log('Polygon created - checking status:', {
              isInFeatureGroup,
              isInLayersRef,
              isOnMap,
              layersCount: layersRef.current.length,
              featureGroupLayers: allFeatureGroupLayers.length,
              duplicateLayersFound: duplicateLayers.length
            });
            
            // If there are duplicate layers, remove them (keep only the current layer)
            if (duplicateLayers.length > 0) {
              console.warn('Found duplicate layers during verification, removing:', duplicateLayers.length);
              duplicateLayers.forEach(dupLayer => {
                try {
                  if (featureGroupRef.current && featureGroupRef.current.hasLayer && featureGroupRef.current.hasLayer(dupLayer)) {
                    featureGroupRef.current.removeLayer(dupLayer);
                  }
                  // Remove from layersRef by comparing coordinates (in case layer reference differs)
                  const dupIndex = layersRef.current.findIndex(refLayer => {
                    if (refLayer === dupLayer) return true;
                    if (!refLayer || !refLayer.getLatLngs) return false;
                    try {
                      return JSON.stringify(refLayer.getLatLngs()) === JSON.stringify(dupLayer.getLatLngs());
                    } catch (e) {
                      return false;
                    }
                  });
                  if (dupIndex > -1) {
                    layersRef.current.splice(dupIndex, 1);
                  }
                  if (mapInstanceRef.current && mapInstanceRef.current.hasLayer && mapInstanceRef.current.hasLayer(dupLayer)) {
                    mapInstanceRef.current.removeLayer(dupLayer);
                  }
                } catch (e) {
                  console.warn('Error removing duplicate during verification:', e);
                }
              });
              
              // Force update after removing duplicates
              updateGeoJson();
              updateStats();
            }
            
            // Ensure layer is tracked in layersRef (check by coordinates, not reference, in case layer reference differs)
            const layerInLayersRef = layersRef.current.find(refLayer => {
              if (refLayer === layer) return true;
              if (!refLayer || !refLayer.getLatLngs) return false;
              try {
                return JSON.stringify(refLayer.getLatLngs()) === currentLayerCoords;
              } catch (e) {
                return false;
              }
            });
            
            // Also check if ANY layer in featureGroup matches these coordinates and is already in layersRef
            const matchingLayerInRef = allFeatureGroupLayers.find(fgLayer => {
              if (!fgLayer || !fgLayer.getLatLngs) return false;
              try {
                const fgCoords = JSON.stringify(fgLayer.getLatLngs());
                if (fgCoords === currentLayerCoords) {
                  // This layer matches coordinates, check if it's in layersRef
                  return layersRef.current.some(refLayer => {
                    if (refLayer === fgLayer) return true;
                    if (!refLayer || !refLayer.getLatLngs) return false;
                    try {
                      return JSON.stringify(refLayer.getLatLngs()) === currentLayerCoords;
                    } catch (e) {
                      return false;
                    }
                  });
                }
              } catch (e) {
                return false;
              }
              return false;
            });
            
            if (!layerInLayersRef && !matchingLayerInRef) {
              console.warn('Layer missing from layersRef, checking if coordinates already processed', {
                currentCoords: currentLayerCoords.substring(0, 100),
                processedCoordsCount: processedCoordsRef.current.size,
                alreadyProcessed: processedCoordsRef.current.has(currentLayerCoords),
                featureGroupLayers: allFeatureGroupLayers.length,
                layersRefCount: layersRef.current.length,
                duplicateLayers: duplicateLayers.length
              });
              
              // Check if these coordinates were already processed
              if (processedCoordsRef.current.has(currentLayerCoords)) {
                console.warn('Layer coordinates already processed, skipping add to prevent duplicate');
                return; // Exit early, don't add
              }
              
              // Check if we're not already at the expected count
              // If we have more layers than expected, we might have duplicates
              const expectedCount = allFeatureGroupLayers.length - duplicateLayers.length;
              if (layersRef.current.length >= expectedCount) {
                console.warn('LayersRef count already matches expected, skipping add to prevent duplicate');
              } else {
                // Mark as processed before adding
                processedCoordsRef.current.add(currentLayerCoords);
                layersRef.current.push(layer);
                console.log('Added layer to layersRef in verification step');
              }
            } else {
              console.log('Layer already in layersRef (found by reference or coordinates, or matching layer exists)', {
                featureGroupLayers: allFeatureGroupLayers.length,
                layersRefCount: layersRef.current.length
              });
            }
            
            // Layer should already be in featureGroup from Leaflet.draw - don't add it again
            // Ensure it's visible on the map
            if (!isOnMap) {
              mapInstanceRef.current.addLayer(layer);
            }
            
            // Ensure layer is visible
            if (layer.setOpacity) {
              layer.setOpacity(1.0);
            }
            if (layer.bringToFront) {
              layer.bringToFront();
            }
            if (layer.redraw) {
              layer.redraw();
            }
          }, 100);
          
          // Enable editing after ensuring map is fully ready
          // Use a flag to prevent multiple enable attempts
          let editingEnabled = false;
          
          const enableEditing = () => {
            // Don't try to enable if already enabled
            if (editingEnabled) return;
            
            // Use the layer reference directly
            if (!layer || !mapInstanceRef.current || !featureGroupRef.current.hasLayer(layer)) {
              // Layer not found - don't retry, it might have been removed
              return;
            }
            
            try {
              const map = mapInstanceRef.current;
              // Ensure map is fully initialized with projection
              if (map && map._loaded && map.options && map.options.crs && map._container) {
                // Double-check layer is still in featureGroup
                if (featureGroupRef.current.hasLayer(layer)) {
                  // Check if editing is already enabled
                  if (layer.editing && layer.editing._enabled) {
                    editingEnabled = true;
                    return;
                  }
                  
                  layer.editing.enable();
                  editingEnabled = true;
                  console.log('Editing enabled successfully');
                }
              } else {
                // Map not ready yet, retry after a delay
                if (!editingEnabled) {
                  setTimeout(enableEditing, 100);
                }
              }
            } catch (error) {
              console.warn('Error enabling editing:', error);
              // Only retry if it's a projection error and we haven't enabled yet
              if (!editingEnabled && error.message && (error.message.includes('project') || error.message.includes('null'))) {
                setTimeout(enableEditing, 150);
              }
            }
          };
          
          // Wait for map to be fully ready before enabling editing
          const map = mapInstanceRef.current;
          if (map) {
            // Use whenReady to ensure map projection is available
            map.whenReady(() => {
              // Wait a bit more to ensure projection is fully initialized
              setTimeout(enableEditing, 150);
            });
            // Fallback timeout
            setTimeout(enableEditing, 300);
          }
        };

        const handleEdited = (e) => {
          if (!mapInstanceRef.current) return;
          
          // Update processed coordinates when a layer is edited
          // The layer's coordinates have changed, so we need to update the tracking
          if (e && e.layers) {
            e.layers.eachLayer((editedLayer) => {
              if (editedLayer && editedLayer.getLatLngs) {
                try {
                  const newCoords = JSON.stringify(editedLayer.getLatLngs());
                  // Remove old coordinates and add new ones
                  // We can't know the old coordinates, so we'll let the verification step handle duplicates
                  processedCoordsRef.current.add(newCoords);
                  console.log('Updated processed coordinates for edited layer');
                } catch (e) {
                  console.warn('Error updating processed coordinates for edited layer:', e);
                }
              }
            });
          }
          
          updateGeoJson();
          saveToHistory();
        };

        const handleDeleted = (e) => {
          if (!mapInstanceRef.current || !featureGroupRef.current) return;
          
          console.log('Delete event received:', e);
          
          // Get all deleted layers from the event
          // Leaflet.draw passes deleted layers in e.layers
          const deletedLayers = e.layers ? e.layers.getLayers() : [];
          
          // If no layers in event, check featureGroup for layers that might have been deleted
          if (deletedLayers.length === 0) {
            // Fallback: check if any layers were removed from featureGroup
            const currentLayers = featureGroupRef.current.getLayers();
            const previousLayers = layersRef.current;
            const removedLayers = previousLayers.filter(prevLayer => 
              !currentLayers.includes(prevLayer)
            );
            removedLayers.forEach(removedLayer => {
              const layerIndex = layersRef.current.indexOf(removedLayer);
              if (layerIndex > -1) {
                layersRef.current.splice(layerIndex, 1);
              }
            });
          } else {
            // Remove all deleted layers from our tracking
            deletedLayers.forEach((deletedLayer) => {
              // Remove from layersRef
              const layerIndex = layersRef.current.indexOf(deletedLayer);
              if (layerIndex > -1) {
                layersRef.current.splice(layerIndex, 1);
                console.log('Removed deleted layer from layersRef');
              }
              
              // Also check for and remove any duplicates with the same coordinates
              const allLayers = featureGroupRef.current.getLayers();
              if (deletedLayer.getLatLngs) {
                const deletedCoords = JSON.stringify(deletedLayer.getLatLngs());
                const duplicates = allLayers.filter(l => 
                  l !== deletedLayer && 
                  l && 
                  l.getLatLngs && 
                  JSON.stringify(l.getLatLngs()) === deletedCoords
                );
                
                if (duplicates.length > 0) {
                  console.warn('Removing duplicate layers after deletion:', duplicates.length);
                  duplicates.forEach(dup => {
                    featureGroupRef.current.removeLayer(dup);
                    const dupIndex = layersRef.current.indexOf(dup);
                    if (dupIndex > -1) {
                      layersRef.current.splice(dupIndex, 1);
                    }
                  });
                }
              }
            });
          }
          
          // Force cleanup: remove any layers from layersRef that are no longer in featureGroup
          const currentFeatureGroupLayers = featureGroupRef.current.getLayers();
          layersRef.current = layersRef.current.filter(layer => 
            currentFeatureGroupLayers.includes(layer)
          );
          
          console.log('After deletion:', {
            layersRefCount: layersRef.current.length,
            featureGroupCount: featureGroupRef.current.getLayers().length
          });
          
          updateGeoJson();
          updateStats();
          saveToHistory();
        };

        map.on(L.Draw.Event.CREATED, handleCreated);
        map.on(L.Draw.Event.EDITED, handleEdited);
        map.on(L.Draw.Event.DELETED, handleDeleted);

        // Store map event handlers for cleanup
        eventHandlersRef.current.push(
          { type: 'map', event: L.Draw.Event.CREATED, handler: handleCreated },
          { type: 'map', event: L.Draw.Event.EDITED, handler: handleEdited },
          { type: 'map', event: L.Draw.Event.DELETED, handler: handleDeleted }
        );
      }
      
      // If map is ready, set up immediately, otherwise wait
      // Need to ensure control containers are initialized
      const trySetup = () => {
        const currentMap = mapInstanceRef.current;
        if (currentMap && currentMap._loaded && currentMap._container && 
            currentMap._controlContainer && currentMap._controlCorners && 
            currentMap._controlCorners.topleft) {
          setupDrawControl();
        } else {
          // Retry after a short delay
          setTimeout(trySetup, 100);
        }
      };
      
      if (map._loaded && map._container) {
        // Small delay to ensure map is fully rendered and control containers are initialized
        setTimeout(trySetup, 100);
      } else {
        map.whenReady(() => {
          setTimeout(trySetup, 200);
        });
        // Fallback in case whenReady doesn't fire
        setTimeout(trySetup, 500);
      }
    } else {
      // Disable drawing
      if (drawControlRef.current) {
        map.removeControl(drawControlRef.current);
        drawControlRef.current = null;
      }
      
      // Clean up map event listeners
      if (map && eventHandlersRef.current.length > 0) {
        eventHandlersRef.current.forEach(({ type, event, handler, layer }) => {
          if (type === 'map' && event && handler) {
            try {
              map.off(event, handler);
            } catch (e) {
              // Ignore errors
            }
          } else if (type === 'layer' && layer && handler) {
            try {
              layer.off(handler.event, handler.handler);
            } catch (e) {
              // Ignore errors
            }
          }
        });
        // Keep only layer handlers (they'll be cleaned up when layers are removed)
        eventHandlersRef.current = eventHandlersRef.current.filter(h => h.type === 'layer');
      }
    }
    
    // Cleanup function
    return () => {
      if (drawControlRef.current && mapInstanceRef.current) {
        try {
          mapInstanceRef.current.removeControl(drawControlRef.current);
        } catch (e) {
          // Ignore errors
        }
        drawControlRef.current = null;
      }
      
      // Clean up map event listeners
      if (mapInstanceRef.current && eventHandlersRef.current.length > 0) {
        eventHandlersRef.current.forEach(({ type, event, handler, layer, handlers }) => {
          if (type === 'map' && event && handler) {
            try {
              mapInstanceRef.current.off(event, handler);
            } catch (e) {
              // Ignore errors
            }
          } else if (type === 'layer' && layer && handlers) {
            try {
              handlers.forEach(({ event, handler }) => {
                layer.off(event, handler);
              });
            } catch (e) {
              // Ignore errors
            }
          }
        });
        eventHandlersRef.current = [];
      }
    };
  }, [editMode]);

  const clearLayers = (clearProcessedCoords = true) => {
    if (!mapInstanceRef.current || !featureGroupRef.current) return;
    
    featureGroupRef.current.clearLayers();
    layersRef.current = [];
    if (clearProcessedCoords) {
      processedCoordsRef.current.clear(); // Clear processed coordinates when clearing layers
      console.log('Cleared processed coordinates');
    } else {
      console.log('Cleared layers but kept processed coordinates for duplicate detection');
    }
  };

  const updateGeoJson = () => {
    if (!mapInstanceRef.current || !mapInstanceRef.current.getContainer() || !featureGroupRef.current) return;

    // Mark this as an internal update to prevent reloading
    isInternalUpdateRef.current = true;

    const features = [];
    
    // Use featureGroup as source of truth
    const allLayers = featureGroupRef.current.getLayers();
    
    // Remove duplicate layers from featureGroup (Leaflet Draw can add dupes when editing properties)
    // Keep the layer with non-empty properties per unique coordinates (preserve user edits)
    const layersByCoords = new Map(); // coordKey -> { layer, hasProps }
    const layersToRemove = [];
    allLayers.forEach(layer => {
      if (!layer || !layer.getLatLngs) return;
      try {
        const coordKey = JSON.stringify(layer.getLatLngs());
        const layerProps = layer.options?.properties || {};
        const hasProps = layerProps && typeof layerProps === 'object' && Object.keys(layerProps).length > 0;
        const existing = layersByCoords.get(coordKey);
        if (!existing) {
          layersByCoords.set(coordKey, { layer, hasProps });
        } else if (hasProps && !existing.hasProps) {
          layersToRemove.push(existing.layer);
          layersByCoords.set(coordKey, { layer, hasProps });
        } else {
          layersToRemove.push(layer);
        }
      } catch (e) {
        // Ignore
      }
    });
    layersToRemove.forEach(dup => {
      featureGroupRef.current.removeLayer(dup);
      const idx = layersRef.current.indexOf(dup);
      if (idx > -1) layersRef.current.splice(idx, 1);
    });
    
    // Build unique layers list (by reference, now that dupes are removed)
    const uniqueLayers = [];
    const seenLayers = new Set();
    featureGroupRef.current.getLayers().forEach(layer => {
      if (layer && layer.getLatLngs && !seenLayers.has(layer)) {
        seenLayers.add(layer);
        uniqueLayers.push(layer);
      }
    });
    layersRef.current = uniqueLayers;
    
    // Process each unique layer only once
    uniqueLayers.forEach(layer => {
      try {
        if (!layer) return;
        
        // Try toGeoJSON first as it's more reliable
        let geoJson;
        try {
          geoJson = layer.toGeoJSON();
          if (geoJson && geoJson.geometry && (geoJson.geometry.type === 'Polygon' || geoJson.geometry.type === 'LineString')) {
            // Leaflet toGeoJSON returns empty properties - must use layer.options.properties (where we store name/locationName)
            const layerProps = layer.options?.properties || geoJson.properties || {};
            const mergedProps = { ...(geoJson.properties || {}), ...layerProps };
            if (mergedProps.name) mergedProps.locationName = mergedProps.name;
            features.push({
              type: 'Feature',
              geometry: geoJson.geometry,
              properties: mergedProps
            });
            return; // Successfully converted, skip manual conversion
          }
        } catch (toGeoJsonError) {
          // If toGeoJSON fails, try manual conversion
          console.log('toGeoJSON failed, trying manual conversion:', toGeoJsonError);
        }
        
        // Manual conversion fallback
        if (!layer.getLatLngs) return;
        
        const latlngs = layer.getLatLngs();
        if (!latlngs || latlngs.length === 0) return;
        
        // Convert Leaflet [lat, lng] format to GeoJSON [lng, lat] format
        let coordinates;
        
        // Check if it's a nested array (multiple rings or array of arrays)
        if (Array.isArray(latlngs[0])) {
          if (Array.isArray(latlngs[0][0])) {
            // Multiple rings (outer ring + holes) - array of arrays of arrays
            coordinates = latlngs.map(ring => {
              if (Array.isArray(ring)) {
                return ring.map(latlng => {
                  if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
                    return [latlng.lng, latlng.lat];
                  } else if (Array.isArray(latlng) && latlng.length >= 2) {
                    return [latlng[1], latlng[0]];
                  }
                  return null;
                }).filter(coord => coord !== null);
              }
              return [];
            }).filter(ring => ring.length > 0);
          } else {
            // Single ring but wrapped in array - array of arrays
            coordinates = [latlngs.map(latlng => {
              if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
                return [latlng.lng, latlng.lat];
              } else if (Array.isArray(latlng) && latlng.length >= 2) {
                return [latlng[1], latlng[0]];
              }
              return null;
            }).filter(coord => coord !== null)];
          }
        } else {
          // Single ring - array of LatLng objects
          coordinates = [latlngs.map(latlng => {
            if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
              return [latlng.lng, latlng.lat];
            } else if (Array.isArray(latlng) && latlng.length >= 2) {
              return [latlng[1], latlng[0]];
            }
            return null;
          }).filter(coord => coord !== null)];
        }
        
        // Ensure we have at least 3 points for a valid polygon
        if (coordinates.length > 0 && coordinates[0] && coordinates[0].length >= 3) {
          // Close the polygon if not already closed
          const firstCoord = coordinates[0][0];
          const lastCoord = coordinates[0][coordinates[0].length - 1];
          if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
            coordinates[0].push([firstCoord[0], firstCoord[1]]);
          }
          
          const layerProps = layer.options?.properties || {};
          const mergedProps = { ...layerProps };
          if (mergedProps.name) mergedProps.locationName = mergedProps.name;
          features.push({
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: coordinates
            },
            properties: mergedProps
          });
        }
      } catch (error) {
        console.warn('Error converting layer to GeoJSON:', error, layer);
      }
    });

    // Deduplicate by geometry (same coordinates = same polygon) to prevent duplicates from echo/reload
    const seenCoords = new Set();
    const dedupedFeatures = features.filter(f => {
      const coordKey = JSON.stringify(f.geometry?.coordinates);
      if (!coordKey || seenCoords.has(coordKey)) return false;
      seenCoords.add(coordKey);
      return true;
    });

    const featureCollection = {
      type: 'FeatureCollection',
      features: dedupedFeatures
    };

    console.log('updateGeoJson called:', {
      featuresCount: dedupedFeatures.length,
      layersRefCount: layersRef.current.length,
      featureGroupCount: featureGroupRef.current ? featureGroupRef.current.getLayers().length : 0
    });
    
    updateStats();
    
    // Mark as internal update and store what we send so we can skip reload on echo
    isInternalUpdateRef.current = true;
    lastSentGeoJsonRef.current = JSON.parse(JSON.stringify(featureCollection));
    if (onGeoJsonChange) {
      onGeoJsonChange(featureCollection);
    }
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 300);
  };

  const updateStats = () => {
    if (!mapInstanceRef.current || !mapInstanceRef.current.getContainer() || !featureGroupRef.current) return;

    let totalAreaKm2 = 0;
    let count = 0;

    // Use featureGroup as source of truth - it contains all layers on the map
    const allLayers = featureGroupRef.current.getLayers();
    
    // Deduplicate layers using Set to track unique layer references
    const seenLayers = new Set();
    const uniqueLayers = [];
    allLayers.forEach(layer => {
      if (layer && layer.getLatLngs && !seenLayers.has(layer)) {
        seenLayers.add(layer);
        uniqueLayers.push(layer);
      }
    });
    
    // Sync layersRef with deduplicated layers
    layersRef.current = uniqueLayers;
    
    // Process each unique layer only once
    uniqueLayers.forEach(layer => {
      try {
        if (!layer || !layer.getLatLngs) return;
        
        // Count if layer exists and has coordinates
        const latlngs = layer.getLatLngs();
        if (latlngs && latlngs.length > 0) {
          // Check if it's in featureGroup (should be, but verify)
          const isInFeatureGroup = featureGroupRef.current && featureGroupRef.current.hasLayer(layer);
          const isOnMap = mapInstanceRef.current.hasLayer(layer);
          
          // Count it if it has valid coordinates (even if not in featureGroup yet)
          count++;
          
          // Try to calculate area
          try {
            // Use getLatLngs directly instead of toGeoJSON to avoid errors during editing
            // Convert to GeoJSON format for area calculation
            let coordinates;
            if (Array.isArray(latlngs[0]) && Array.isArray(latlngs[0][0])) {
              // Multiple rings
              coordinates = latlngs.map(ring => {
                if (Array.isArray(ring)) {
                  return ring.map(latlng => {
                    if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
                      return [latlng.lng, latlng.lat];
                    } else if (Array.isArray(latlng) && latlng.length >= 2) {
                      return [latlng[1], latlng[0]];
                    }
                    return null;
                  }).filter(coord => coord !== null);
                }
                return [];
              }).filter(ring => ring.length > 0);
            } else {
              // Single ring
              coordinates = [latlngs.map(latlng => {
                if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
                  return [latlng.lng, latlng.lat];
                } else if (Array.isArray(latlng) && latlng.length >= 2) {
                  return [latlng[1], latlng[0]];
                }
                return null;
              }).filter(coord => coord !== null)];
            }
            
            if (coordinates.length > 0 && coordinates[0].length >= 3) {
              const geoJson = {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: coordinates
                }
              };
              const area = turf.area(geoJson);
              totalAreaKm2 += area;
            }
          } catch (error) {
            // Silently ignore area calculation errors during editing
            // The count is more important than the area during active editing
          }
        }
      } catch (error) {
        // Silently ignore errors - just don't count this layer
      }
    });

    setTotalArea(totalAreaKm2);
    setFeatureCount(count);
    console.log('Stats updated:', { count, totalAreaKm2, layersRefCount: layersRef.current.length });
  };

  const saveToHistory = () => {
    const currentGeoJson = getCurrentGeoJson();
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(currentGeoJson));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const getCurrentGeoJson = () => {
    if (!featureGroupRef.current) {
      return { type: 'FeatureCollection', features: [] };
    }
    
    const features = [];
    // Use featureGroup as source of truth - it contains all layers on the map
    const allLayers = featureGroupRef.current.getLayers();
    
    // Deduplicate layers using Set to track unique layer references
    const seenLayers = new Set();
    const uniqueLayers = [];
    allLayers.forEach(layer => {
      if (layer && !seenLayers.has(layer)) {
        seenLayers.add(layer);
        uniqueLayers.push(layer);
      }
    });
    
    // Process each unique layer only once
    uniqueLayers.forEach(layer => {
      try {
        if (!layer || !layer.getLatLngs) return;
        
        // Get coordinates directly from Leaflet layer to preserve precision
        const latlngs = layer.getLatLngs();
        if (!latlngs || latlngs.length === 0) return;
        
        // Convert Leaflet [lat, lng] format to GeoJSON [lng, lat] format
        let coordinates;
        if (Array.isArray(latlngs[0]) && Array.isArray(latlngs[0][0])) {
          // Multiple rings (outer ring + holes)
          coordinates = latlngs.map(ring => {
            if (Array.isArray(ring)) {
              return ring.map(latlng => {
                if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
                  return [latlng.lng, latlng.lat]; // Convert to [lng, lat]
                } else if (Array.isArray(latlng) && latlng.length >= 2) {
                  return [latlng[1], latlng[0]]; // Already [lat, lng], convert to [lng, lat]
                }
                return null;
              }).filter(coord => coord !== null);
            }
            return [];
          }).filter(ring => ring.length > 0);
        } else {
          // Single ring (outer ring only)
          coordinates = [latlngs.map(latlng => {
            if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
              return [latlng.lng, latlng.lat]; // Convert to [lng, lat]
            } else if (Array.isArray(latlng) && latlng.length >= 2) {
              return [latlng[1], latlng[0]]; // Already [lat, lng], convert to [lng, lat]
            }
            return null;
          }).filter(coord => coord !== null)];
        }
        
        // Ensure we have at least 3 points for a valid polygon
        if (coordinates.length > 0 && coordinates[0].length >= 3) {
          features.push({
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: coordinates
            },
            properties: layer.options?.properties || {}
          });
        }
      } catch (error) {
        console.warn('Error converting layer to GeoJSON in getCurrentGeoJson:', error);
        // Fallback to Leaflet's toGeoJSON if direct conversion fails
        try {
          const geoJson = layer.toGeoJSON();
          if (geoJson.geometry && geoJson.geometry.type === 'Polygon') {
            features.push({
              type: 'Feature',
              geometry: geoJson.geometry,
              properties: geoJson.properties || {}
            });
          }
        } catch (fallbackError) {
          console.warn('Fallback GeoJSON conversion also failed:', fallbackError);
        }
      }
    });
    return {
      type: 'FeatureCollection',
      features: features
    };
  };

  const loadGeoJsonToMap = (geoJson) => {
    if (!mapInstanceRef.current || !featureGroupRef.current) return;
    
    // Clean up existing layer event handlers before clearing
    layersRef.current.forEach(layer => {
      try {
        layer.off(); // Remove all event listeners from layer
      } catch (e) {
        // Ignore errors
      }
    });
    
    clearLayers();
    
    // Ensure featureGroup is on the map before adding layers
    if (mapInstanceRef.current && featureGroupRef.current && !mapInstanceRef.current.hasLayer(featureGroupRef.current)) {
      featureGroupRef.current.addTo(mapInstanceRef.current);
      console.log('Added featureGroup to map');
    }
    
    if (geoJson && geoJson.features && Array.isArray(geoJson.features)) {
      console.log(`Loading ${geoJson.features.length} features to map`);
      geoJson.features.forEach((feature, index) => {
        if (!mapInstanceRef.current || !featureGroupRef.current) return;
        
        if (feature.geometry && feature.geometry.type === 'Polygon') {
          try {
            const coordinates = feature.geometry.coordinates[0];
            if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0) return;
            
            const latlngs = coordinates.map(coord => {
              if (!Array.isArray(coord) || coord.length < 2) return null;
              return [coord[1], coord[0]];
            }).filter(coord => coord !== null);
            
            if (latlngs.length < 3) return; // Need at least 3 points for a polygon
            
            const polygon = L.polygon(latlngs, {
              color: '#3388ff',
              weight: 2,
              fillColor: '#3388ff',
              fillOpacity: 0.3,
              opacity: 1.0,
              interactive: true,
              bubblingMouseEvents: true,
              properties: feature.properties || {}
            });
            
            // Ensure polygon is visible with blue opacity
            polygon.setStyle({
              color: '#3388ff',
              weight: 2,
              fillColor: '#3388ff',
              fillOpacity: 0.3,
              opacity: 1.0,
              interactive: true
            });
            
            // Bring polygon to front to ensure visibility
            polygon.bringToFront();

            // Add polygon to featureGroup (which is already on the map)
            // Don't add directly to map - featureGroup handles that
            // Check if already in featureGroup to avoid duplicates
            if (!mapInstanceRef.current || !featureGroupRef.current) return;
            if (!featureGroupRef.current.hasLayer(polygon)) {
              featureGroupRef.current.addLayer(polygon);
              console.log(`Polygon ${index + 1}/${geoJson.features.length} added to featureGroup`, { 
                latlngsCount: latlngs.length,
                hasLayer: featureGroupRef.current.hasLayer(polygon),
                isOnMap: mapInstanceRef.current && mapInstanceRef.current.hasLayer ? mapInstanceRef.current.hasLayer(polygon) : false,
                featureGroupOnMap: mapInstanceRef.current && mapInstanceRef.current.hasLayer ? mapInstanceRef.current.hasLayer(featureGroupRef.current) : false,
                featureGroupLayerCount: featureGroupRef.current.getLayers().length
              });
            }
            if (layersRef.current.indexOf(polygon) === -1) {
              layersRef.current.push(polygon);
            }
            
            // Verify polygon is actually in featureGroup after adding
            setTimeout(() => {
              if (!mapInstanceRef.current || !featureGroupRef.current) return;
              const isInFeatureGroup = featureGroupRef.current.hasLayer(polygon);
              const featureGroupLayers = featureGroupRef.current.getLayers();
              if (!isInFeatureGroup || !featureGroupLayers.includes(polygon)) {
                console.warn(`Polygon ${index + 1} not in featureGroup after adding, re-adding...`);
                if (!featureGroupRef.current.hasLayer(polygon)) {
                  featureGroupRef.current.addLayer(polygon);
                }
              }
            }, 50);
            
            // Ensure polygon is visible - bring to front and redraw
            polygon.bringToFront();
            if (polygon.redraw) {
              polygon.redraw();
            }
            
            // Now enable editing after the polygon is added to the map
            // Wait for map to be fully initialized with projection
            const enableEditing = () => {
              if (!polygon || !mapInstanceRef.current || !featureGroupRef.current.hasLayer(polygon)) {
                return;
              }
              
              try {
                // Check if map has a valid projection/crs before enabling editing
                const map = mapInstanceRef.current;
                // Ensure map is fully initialized - check for _loaded, options, and crs
                if (map && map._loaded && map.options && map.options.crs && map._container) {
                  // Double-check that polygon is still on the map
                  if (featureGroupRef.current.hasLayer(polygon)) {
                    polygon.editing.enable();
                  }
                } else {
                  // If map not ready, wait a bit more
                  setTimeout(enableEditing, 50);
                }
              } catch (error) {
                console.warn('Error enabling editing on polygon:', error);
                // Retry after a short delay if error is about projection
                if (error.message && (error.message.includes('project') || error.message.includes('null'))) {
                  setTimeout(enableEditing, 100);
                }
              }
            };
            
            // Wait for map to be fully loaded and rendered
            const map = mapInstanceRef.current;
            if (map) {
              // Use whenReady to ensure map is fully initialized
              map.whenReady(() => {
                // Additional check to ensure projection is ready
                setTimeout(enableEditing, 100);
              });
              // Fallback timeout in case whenReady doesn't work
              setTimeout(enableEditing, 200);
            }
            
            // Make polygon clickable: show properties popup (when editableProperties) or zoom to it
            polygon.on('click', (e) => {
              L.DomEvent.stopPropagation(e);
              const map = mapInstanceRef.current;
              if (!map) return;

              if (editableProperties) {
                // Show popup to edit polygon properties (name)
                const currentName = polygon.options?.properties?.name || polygon.options?.properties?.name_en || '';
                const safeValue = String(currentName || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                const container = document.createElement('div');
                container.className = 'leaflet-popup-properties-edit';
                container.innerHTML = `
                  <label style="display:block;margin-bottom:6px;font-weight:600;font-size:13px">Location name</label>
                  <input type="text" class="polygon-name-input" value="${safeValue}" placeholder="Enter location name" style="width:100%;padding:8px 10px;margin-bottom:10px;font-size:14px;border:1px solid #ddd;border-radius:4px;box-sizing:border-box" />
                  <button type="button" class="polygon-properties-save-btn" style="padding:8px 16px;background:#3388ff;color:white;border:none;border-radius:4px;cursor:pointer;font-size:14px">Save</button>
                `;
                const input = container.querySelector('.polygon-name-input');
                const saveBtn = container.querySelector('.polygon-properties-save-btn');
                const popup = L.popup({ maxWidth: 280 }).setLatLng(e.latlng).setContent(container);
                saveBtn.onclick = () => {
                  const name = input.value.trim();
                  polygon.options.properties = polygon.options.properties || {};
                  if (name) {
                    polygon.options.properties.name = name;
                    polygon.options.properties.locationName = name;
                  } else {
                    delete polygon.options.properties.name;
                    delete polygon.options.properties.locationName;
                  }
                  popup.close();
                  updateGeoJson();
                };
                popup.openOn(map);
              } else if (polygon.getBounds) {
                try {
                  const bounds = polygon.getBounds();
                  if (bounds && bounds.isValid && bounds.isValid()) {
                    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
                  }
                } catch (error) {
                  const latlngs = polygon.getLatLngs();
                  if (latlngs?.[0]?.[0]) map.setView(latlngs[0][0], 12);
                }
              }
            });
            
            // Add hover effect to show it's clickable
            polygon.on('mouseover', () => {
              polygon.setStyle({
                weight: 3,
                fillOpacity: 0.4
              });
            });
            
            polygon.on('mouseout', () => {
              polygon.setStyle({
                weight: 2,
                fillOpacity: 0.3
              });
            });
            
            const handleEdit = () => {
              if (!mapInstanceRef.current) return;
              updateGeoJson();
              saveToHistory();
              // Update stats after edit is complete
              setTimeout(() => updateStats(), 0);
            };
            
            const handleDragEnd = () => {
              if (!mapInstanceRef.current) return;
              updateGeoJson();
              saveToHistory();
            };
            
            const handleContextMenu = (e) => {
              e.originalEvent.preventDefault();
              if (!mapInstanceRef.current || !featureGroupRef.current) return;
              if (window.confirm('Delete this polygon?')) {
                featureGroupRef.current.removeLayer(polygon);
                const layerIndex = layersRef.current.indexOf(polygon);
                if (layerIndex > -1) {
                  layersRef.current.splice(layerIndex, 1);
                }
                // Remove from event handlers
                eventHandlersRef.current = eventHandlersRef.current.filter(
                  h => !(h.type === 'layer' && h.layer === polygon)
                );
                updateGeoJson();
                saveToHistory();
              }
            };

            polygon.on('edit', handleEdit);
            polygon.on('dragend', handleDragEnd);
            polygon.on('contextmenu', handleContextMenu);

            // Store handlers for cleanup
            eventHandlersRef.current.push({
              type: 'layer',
              layer: polygon,
              handlers: [
                { event: 'edit', handler: handleEdit },
                { event: 'dragend', handler: handleDragEnd },
                { event: 'contextmenu', handler: handleContextMenu }
              ]
            });
          } catch (error) {
            console.error('Error loading polygon:', error);
          }
        }
      });
    }
    updateStats();
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const geoJson = JSON.parse(history[newIndex]);
      loadGeoJsonToMap(geoJson);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const geoJson = JSON.parse(history[newIndex]);
      loadGeoJsonToMap(geoJson);
    }
  };

  const handleClear = () => {
    if (window.confirm('Clear all polygons? This cannot be undone.')) {
      clearLayers();
      updateGeoJson();
      saveToHistory();
    }
  };

  const formatArea = (areaKm2) => {
    if (areaKm2 < 1) {
      return `${(areaKm2 * 100).toFixed(2)} hectares`;
    } else if (areaKm2 < 100) {
      return `${areaKm2.toFixed(2)} km²`;
    } else {
      return `${(areaKm2 / 100).toFixed(2)} km²`;
    }
  };

  return (
    <div className="map-editor-container">
      <div className="map-editor-controls">
        <div className="map-editor-controls-left">
          {/* View Mode button removed - always in edit mode when MapEditor is used */}
          {editMode && (
            <>
              <button
                type="button"
                className="btn-action"
                onClick={handleUndo}
                disabled={historyIndex <= 0}
                title="Undo"
              >
                <i className="fas fa-undo"></i>
              </button>
              <button
                type="button"
                className="btn-action"
                onClick={handleRedo}
                disabled={historyIndex >= history.length - 1}
                title="Redo"
              >
                <i className="fas fa-redo"></i>
              </button>
              <button
                type="button"
                className="btn-action"
                onClick={handleClear}
                title="Clear All"
              >
                <i className="fas fa-trash"></i>
              </button>
            </>
          )}
        </div>
        <div className="map-editor-stats">
          <span 
            onClick={() => {
              if (featureCount > 0 && layersRef.current.length > 0 && mapInstanceRef.current) {
                // Find first polygon and zoom to it
                const firstPolygon = layersRef.current[0];
                if (firstPolygon && firstPolygon.getBounds) {
                  try {
                    const bounds = firstPolygon.getBounds();
                    if (bounds && bounds.isValid && bounds.isValid()) {
                      mapInstanceRef.current.fitBounds(bounds, { 
                        padding: [50, 50],
                        maxZoom: 15
                      });
                    }
                  } catch (error) {
                    console.warn('Error zooming to first polygon:', error);
                    // Fallback: pan to polygon center
                    const latlngs = firstPolygon.getLatLngs();
                    if (latlngs && latlngs[0] && latlngs[0].length > 0) {
                      const center = latlngs[0][0];
                      mapInstanceRef.current.setView(center, 12);
                    }
                  }
                }
                if (onPolygonCountClick) {
                  onPolygonCountClick();
                }
              }
            }}
            style={{ 
              cursor: featureCount > 0 ? 'pointer' : 'default',
              textDecoration: featureCount > 0 ? 'underline' : 'none',
              color: featureCount > 0 ? '#3388ff' : 'inherit'
            }}
            title={featureCount > 0 ? 'Click to zoom to first polygon' : ''}
          >
            {featureCount} polygon{featureCount !== 1 ? 's' : ''}
          </span>
          {totalArea > 0 && <span> • {formatArea(totalArea)}</span>}
        </div>
        {showAdvanced && (
          <div className="map-editor-controls-right">
            <button
              type="button"
              className="btn-link"
              onClick={onAdvancedToggle}
            >
              Advanced: Edit GeoJSON
            </button>
          </div>
        )}
      </div>
      <div 
        ref={mapRef} 
        className="map-editor-map"
        style={{ height }}
      />
      {editMode && (
        <div className="map-editor-hint">
          <i className="fas fa-info-circle"></i>
          {editExistingHint 
            ? (editableProperties 
              ? 'Click a polygon to edit its name. Click the pencil icon (Edit) to edit vertices. Right-click polygon to delete.'
              : 'Click the pencil icon (Edit) in the map toolbar, then click the polygon to edit vertices. Drag vertices to adjust. Right-click polygon to delete.')
            : (editableProperties 
              ? 'Click the polygon tool to draw. Click a polygon to edit its name. Right-click to delete.'
              : 'Click the polygon tool to draw, drag vertices to edit, right-click to delete')}
        </div>
      )}
    </div>
  );
};

export default MapEditor;
